import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { GoogleAccountConnection } from '../../components/GoogleAccountConnection';
import { API } from '../../utils/api';
import { trackProfileUpdated } from '../../utils/analytics';
import axios from 'axios';
import { CheckCircle, User, Globe, ArrowsClockwise, Warning, DownloadSimple, Trash } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export default function ProfilePage() {
  const { language, setLanguage, t } = useLanguage();
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    preferred_language: user?.preferred_language || language,
    province: user?.province || 'ontario'
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [checklistRefreshed, setChecklistRefreshed] = useState(false);
  const [originalProvince, setOriginalProvince] = useState(user?.province || 'ontario');

  // Account controls state
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dangerError, setDangerError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setSaveError('');
    setChecklistRefreshed(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError('');
    setChecklistRefreshed(false);
    
    try {
      const response = await axios.patch(`${API}/user/profile`, formData);

      const updatedFields = [];
      if (formData.first_name !== user?.first_name) updatedFields.push('first_name');
      if (formData.last_name !== user?.last_name) updatedFields.push('last_name');
      if (formData.preferred_language !== user?.preferred_language) updatedFields.push('language');
      if (formData.province !== originalProvince) updatedFields.push('province');
      
      if (updatedFields.length > 0) {
        trackProfileUpdated(updatedFields);
      }
      
      updateUser({ ...response.data, google_connected: Boolean(response.data.google_id) });
      setLanguage(formData.preferred_language);
      setSaved(true);
      setOriginalProvince(response.data.province || formData.province);
      
      if (response.data.checklist_refreshed) {
        setChecklistRefreshed(true);
      }
    } catch (err) {
      setSaveError(err.response?.status === 409 ? 'conflict' : 'failed');
    } finally {
      setSaving(false);
    }
  };

  const provinceChanged = formData.province !== originalProvince;

  const handleDownloadData = async () => {
    setDangerError('');
    setExporting(true);
    try {
      const response = await axios.get(`${API}/user/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paperpal-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDangerError(err.response?.data?.detail || (language === 'fr' ? 'Échec du téléchargement de vos données.' : 'Failed to download your data.'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDangerError('');
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE MY ACCOUNT') {
      setDangerError(language === 'fr'
        ? 'Veuillez taper exactement "DELETE MY ACCOUNT" pour confirmer.'
        : 'Please type "DELETE MY ACCOUNT" exactly to confirm.');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/user/account`, {
        data: { confirm: true, confirmation_phrase: 'DELETE MY ACCOUNT' },
      });
      // Local logout to clear any cached user state
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      setDangerError(err.response?.data?.detail || (language === 'fr' ? 'Échec de la suppression du compte.' : 'Failed to delete account.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-24 pb-12">
        <div className="pp-container max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="pp-heading-2 text-pp-text mb-8" data-testid="profile-title">
              {t('profile.title')}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Info */}
              <div className="pp-card">
                <div className="flex items-center gap-4 mb-6">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.first_name}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-pp-navy-light flex items-center justify-center">
                      <User size={32} className="text-pp-navy" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-pp-text">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-sm text-pp-text-secondary">{user?.email}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="pp-label">
                      {t('auth.firstName')}
                    </Label>
                    <Input
                      id="first_name"
                      disabled={saving}
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      className="pp-input"
                      data-testid="profile-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="pp-label">
                      {t('auth.lastName')}
                    </Label>
                    <Input
                      id="last_name"
                      disabled={saving}
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      className="pp-input"
                      data-testid="profile-lastname"
                    />
                  </div>
                </div>
                <GoogleAccountConnection />
              </div>

              {/* Settings */}
              <div className="pp-card">
                <h2 className="pp-heading-4 text-pp-text mb-4">{t('profile.settings')}</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="pp-label flex items-center gap-2">
                      <Globe size={16} />
                      {t('profile.language')}
                    </Label>
                    <Select 
                      disabled={saving}
                      value={formData.preferred_language} 
                      onValueChange={(value) => handleChange('preferred_language', value)}
                    >
                      <SelectTrigger className="pp-input" data-testid="language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en" data-testid="profile-language-option-en">English</SelectItem>
                        <SelectItem value="fr" data-testid="profile-language-option-fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="pp-label">Province</Label>
                    <Select 
                      disabled={saving}
                      value={formData.province} 
                      onValueChange={(value) => handleChange('province', value)}
                    >
                      <SelectTrigger className="pp-input" data-testid="province-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent data-testid="profile-province-options">
                        <SelectItem value="ontario" data-testid="profile-province-option-ontario">Ontario</SelectItem>
                        <SelectItem value="quebec" data-testid="profile-province-option-quebec">Quebec</SelectItem>
                        <SelectItem value="british_columbia" data-testid="profile-province-option-british-columbia">British Columbia</SelectItem>
                        <SelectItem value="alberta" data-testid="profile-province-option-alberta">Alberta</SelectItem>
                        <SelectItem value="manitoba" data-testid="profile-province-option-manitoba">Manitoba</SelectItem>
                        <SelectItem value="saskatchewan" data-testid="profile-province-option-saskatchewan">Saskatchewan</SelectItem>
                        <SelectItem value="nova_scotia" data-testid="profile-province-option-nova-scotia">Nova Scotia</SelectItem>
                        <SelectItem value="new_brunswick" data-testid="profile-province-option-new-brunswick">New Brunswick</SelectItem>
                        <SelectItem value="other" data-testid="profile-province-option-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {provinceChanged && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="profile-province-change-warning">
                        <Warning size={12} />
                        {language === 'fr' 
                          ? 'Votre liste de tâches sera mise à jour avec des informations spécifiques à la province'
                          : 'Your checklist will be updated with province-specific information'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex flex-col gap-3">
                {saveError && <p role="alert" className="text-sm text-red-700" data-testid="profile-save-error">
                  {saveError === 'conflict'
                    ? (language === 'fr' ? 'Votre liste a changé pendant l’enregistrement. Votre profil n’a pas été enregistré. Réessayez.' : 'Your checklist changed while saving. Your profile was not saved. Please try again.')
                    : (language === 'fr' ? 'L’enregistrement de votre profil n’a pas pu être confirmé. Vos modifications sont toujours ici. Réessayez.' : 'Your profile save could not be confirmed. Your changes are still here. Please try again.')}
                </p>}
                <div className="flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="pp-btn-primary"
                    data-testid="save-profile-button"
                  >
                    {saving ? t('common.loading') : t('profile.save')}
                  </button>
                  
                  {saved && (
                    <div className="flex items-center gap-2 text-pp-success-text" role="status" data-testid="profile-save-success">
                      <CheckCircle size={18} weight="fill" />
                      <span className="text-sm">{t('profile.saved')}</span>
                    </div>
                  )}
                </div>
                
                {checklistRefreshed && (
                  <div className="flex items-center gap-2 text-blue-600 bg-blue-50 p-3 rounded-lg" role="status" data-testid="profile-checklist-refreshed">
                    <ArrowsClockwise size={18} />
                    <span className="text-sm">
                      {language === 'fr'
                        ? 'Votre liste de tâches a été mise à jour avec les informations de votre nouvelle province. Votre progression a été préservée.'
                        : 'Your checklist has been updated with your new province information. Your progress has been preserved.'}
                    </span>
                  </div>
                )}
              </div>
            </form>

            {/* Account controls */}
            <div className="pp-card mt-6" data-testid="account-controls-card">
              <h2 className="pp-heading-4 text-pp-text mb-2">
                {language === 'fr' ? 'Vos données' : 'Your data'}
              </h2>
              <p className="text-sm text-pp-text-secondary mb-4">
                {language === 'fr'
                  ? 'Téléchargez une copie de toutes les données PaperPal associées à votre compte.'
                  : 'Download a copy of every PaperPal record tied to your account.'}
              </p>
              <button
                type="button"
                onClick={handleDownloadData}
                disabled={exporting}
                className="pp-btn-secondary inline-flex items-center gap-2"
                data-testid="download-data-button"
              >
                <DownloadSimple size={18} />
                {exporting
                  ? (language === 'fr' ? 'Préparation…' : 'Preparing…')
                  : (language === 'fr' ? 'Télécharger mes données' : 'Download my data')}
              </button>
            </div>

            <div
              className="mt-6 rounded-2xl border border-red-200 bg-red-50/50 p-6"
              data-testid="danger-zone-card"
            >
              <h2 className="pp-heading-4 text-red-700 mb-2 flex items-center gap-2">
                <Warning size={20} weight="fill" />
                {language === 'fr' ? 'Zone dangereuse' : 'Danger zone'}
              </h2>
              <p className="text-sm text-red-800/80 mb-4">
                {language === 'fr'
                  ? 'La suppression de votre compte efface définitivement votre profil, votre liste de tâches, vos documents et vos notes. Cette action est irréversible.'
                  : 'Deleting your account permanently removes your profile, checklist, documents, and notes. This cannot be undone.'}
              </p>
              <button
                type="button"
                onClick={() => { setDangerError(''); setDeleteConfirmText(''); setShowDeleteModal(true); }}
                className="inline-flex items-center gap-2 rounded-full border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors"
                data-testid="open-delete-account-modal"
              >
                <Trash size={18} />
                {language === 'fr' ? 'Supprimer mon compte' : 'Delete my account'}
              </button>
              {dangerError && (
                <p className="mt-3 text-sm text-red-700" data-testid="danger-error">{dangerError}</p>
              )}
            </div>

            {showDeleteModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                data-testid="delete-account-modal"
              >
                <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                  <h3 className="pp-heading-4 text-pp-text mb-2">
                    {language === 'fr' ? 'Confirmer la suppression' : 'Confirm account deletion'}
                  </h3>
                  <p className="text-sm text-pp-text-secondary mb-4">
                    {language === 'fr'
                      ? 'Tapez DELETE MY ACCOUNT pour confirmer. Cette action est définitive.'
                      : 'Type DELETE MY ACCOUNT to confirm. This action is permanent.'}
                  </p>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="pp-input mb-4"
                    data-testid="delete-confirm-input"
                  />
                  {dangerError && (
                    <p className="mb-3 text-sm text-red-700" data-testid="delete-modal-error">{dangerError}</p>
                  )}
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowDeleteModal(false); setDangerError(''); }}
                      className="pp-btn-secondary"
                      data-testid="cancel-delete-account"
                    >
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      data-testid="confirm-delete-account"
                    >
                      <Trash size={16} />
                      {deleting
                        ? (language === 'fr' ? 'Suppression…' : 'Deleting…')
                        : (language === 'fr' ? 'Supprimer définitivement' : 'Permanently delete')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
